import React, { useState, useEffect, useMemo } from 'react';
import { useEnterKeyNavigation } from '../utils/enterKeyNavigation';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useQuery, useMutation } from '@apollo/client/react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Drawer, Grid, Button, Badge, Popover, List, Modal, Form, Input, message } from 'antd';
import {
  GET_UNREAD_NOTIFICATION_COUNT,
  GET_MY_NOTIFICATIONS,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
} from '../apollo/operations/fieldAllocation.operations';
import { CHANGE_PASSWORD_MUTATION } from '../apollo/operations/auth.operations';
import {
  DashboardOutlined,
  DatabaseOutlined,
  SwapOutlined,
  FileTextOutlined,
  SettingOutlined,
  UserOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  InboxOutlined,
  ExportOutlined,
  ImportOutlined,
  BarcodeOutlined,
  TeamOutlined,
  ShopOutlined,
  AppstoreOutlined,
  CloseOutlined,
  CalculatorOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  UserSwitchOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  ApartmentOutlined,
  ExperimentOutlined,
  PushpinOutlined,
  SafetyOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

/** Notification Bell — shows unread count badge + popover with recent notifications */
const NotificationBell: React.FC<{ isMobile: boolean; navigate: (path: string) => void }> = ({ isMobile, navigate }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { data: countData } = useQuery<{ unreadNotificationCount: { count: number } }>(GET_UNREAD_NOTIFICATION_COUNT, { pollInterval: 60000 });
  const { data: notifData, refetch } = useQuery<{ myNotifications: { items: any[] } }>(GET_MY_NOTIFICATIONS, {
    variables: { unreadOnly: false, pagination: { page: 1, limit: 5 } },
    skip: !popoverOpen,
  });
  const [markRead] = useMutation(MARK_NOTIFICATION_READ);
  const [markAllRead] = useMutation(MARK_ALL_NOTIFICATIONS_READ);

  const unreadCount = countData?.unreadNotificationCount?.count || 0;
  const notifications = notifData?.myNotifications?.items || [];

  const handleClick = async (notif: any) => {
    if (!notif.isRead) {
      await markRead({ variables: { notificationId: notif.id } });
    }
    setPopoverOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    refetch();
  };

  const content = (
    <div style={{ width: 320, maxHeight: 400, overflow: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 0 8px 0' }}>
        <Text strong>Notifications</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={handleMarkAllRead}>Mark all read</Button>
        )}
      </div>
      <List
        dataSource={notifications}
        locale={{ emptyText: 'No notifications' }}
        renderItem={(item: any) => (
          <List.Item
            onClick={() => handleClick(item)}
            style={{ cursor: 'pointer', background: item.isRead ? 'transparent' : '#f0f5ff', padding: '8px' }}
          >
            <List.Item.Meta
              title={<Text strong={!item.isRead} style={{ fontSize: 13 }}>{item.title}</Text>}
              description={<Text type="secondary" style={{ fontSize: 12 }}>{item.body}</Text>}
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={popoverOpen}
      onOpenChange={(open) => { setPopoverOpen(open); if (open) refetch(); }}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <BellOutlined style={{ fontSize: isMobile ? 16 : 18, cursor: 'pointer' }} />
      </Badge>
    </Popover>
  );
};

// ─── Permission-based sidebar filtering ──────────────────────────────────────

/** Maps each sidebar leaf key to its backend menu_code. */
const ROUTE_TO_MENU_CODE: Record<string, string> = {
  '/dashboard':                        'dashboard',
  '/master/products':                  'master.products',
  '/master/categories':                'master.categories',
  '/master/vendors':                   'master.vendors',
  '/master/organizations':             'master.organizations',
  '/master/warehouses':                'master.warehouses',
  '/master/uom':                       'master.uom',
  '/master/employees':                 'master.employees',
  '/master/countries':                 'master.countries',
  '/master/states':                    'master.states',
  '/master/cities':                    'master.cities',
  '/master/areas':                     'master.areas',
  '/master/lookup':                    'master.lookup',
  '/master/customers':                 'master.customers',
  '/stock/inward':                     'stock.inward',
  '/stock/issue':                      'stock.issue',
  '/stock/transfer':                   'stock.transfer',
  '/stock/return':                     'stock.return',
  '/requisition':                      'requisition.list',
  '/requisition/approvals':            'requisition.approvals',
  '/purchase':                         'purchase.orders',
  '/purchase/approvals':               'purchase.approvals',
  '/field-allocation/new-request':     'field.allocation',
  '/field-allocation/my-requests':     'field.allocation',
  '/field-allocation':                 'field.allocation',
  '/field-allocation/site-usage/new':  'field.allocation',
  '/field-allocation/site-usage':      'field.allocation',
  '/serial':                           'serial.numbers',
  '/customer-equipment':               'customer.equipment',
  '/customer-deallocation':            'customer.deallocation',
  '/qc-queue':                         'customer.qcqueue',
  '/reports/ledger':                   'reports.ledger',
  '/reports/warehouse':                'reports.warehouse',
  '/reports/movement':                 'reports.movement',
  '/reports/inward':                   'reports.inward',
  '/reports/issue':                    'reports.issue',
  '/reports/return':                   'reports.return',
  '/reports/serial':                   'reports.serial',
  '/reports/employee-stock':           'reports.employee-stock',
  '/reports/rejected-serials':         'reports.rejected-serials',
  '/reports/allocation':               'reports.allocation',
  '/reports/deallocation':             'reports.deallocation',
  '/reports/field-allocation':         'reports.field-allocation',
  '/reports/site-usage':               'reports.site-usage',
  '/settings':                         'settings',
  '/admin/users':                      'admin.users',
  '/admin/permissions':                'admin.permissions',
};

type AnyMenuItem = { key: string; children?: AnyMenuItem[]; [key: string]: any };

/**
 * Recursively filters menu items by the user's `get` permission bits.
 * - While loading: returns all items (avoids layout flash)
 * - Group items: visible only if ≥1 child is visible
 * - Leaf items: visible only if hasPermission(menuCode, 'get')
 * - Items without a mapping (api-docs): always visible
 */
function filterMenuItems(
  items: AnyMenuItem[],
  hasPermission: (code: string, mode: 'get') => boolean,
  isLoading: boolean,
): AnyMenuItem[] {
  if (isLoading) return items;
  return items
    .map(item => {
      if (item.key === '__api-docs__') return item;
      if (item.children) {
        const visible = filterMenuItems(item.children, hasPermission, isLoading);
        return visible.length > 0 ? { ...item, children: visible } : null;
      }
      const code = ROUTE_TO_MENU_CODE[item.key];
      if (!code) return item;
      return hasPermission(code, 'get') ? item : null;
    })
    .filter(Boolean) as AnyMenuItem[];
}

// ─────────────────────────────────────────────────────────────────────────────

interface MainLayoutProps {
  companyName?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  companyName = 'ReliableSoft Technologies Pvt. Ltd.',
}) => {
  useEnterKeyNavigation();
  const { currentUser, logout } = useAuth();
  const userName = currentUser?.username ?? 'User';
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();

  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [changePwdForm] = Form.useForm();
  const [changePassword, { loading: changePwdLoading }] = useMutation(CHANGE_PASSWORD_MUTATION);

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string }) => {
    try {
      const { data } = await changePassword({
        variables: { currentPassword: values.currentPassword, newPassword: values.newPassword },
      });
      if (data?.changePassword?.success) {
        message.success('Password changed successfully');
        setChangePwdOpen(false);
        changePwdForm.resetFields();
      } else {
        message.error(data?.changePassword?.message || 'Failed to change password');
      }
    } catch (err: unknown) {
      const gqlErr = err as { graphQLErrors?: Array<{ message: string; extensions?: { code: string } }> };
      const code = gqlErr?.graphQLErrors?.[0]?.extensions?.code;
      if (code === 'INVALID_CREDENTIALS') {
        message.error('Current password is incorrect.');
      } else if (code === 'UNAUTHENTICATED') {
        message.error('Session expired — please log in again.');
      } else {
        const msg = gqlErr?.graphQLErrors?.[0]?.message;
        message.error(msg || 'Failed to change password');
      }
    }
  };

  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();

  // Determine if we're on mobile (below md breakpoint)
  const isMobile = !screens.md;

  // Close drawer when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setDrawerVisible(false);
    }
  }, [location.pathname, isMobile]);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'master',
      icon: <DatabaseOutlined />,
      label: 'Master Data',
      children: [
        { key: '/master/products', icon: <AppstoreOutlined />, label: 'Products' },
        { key: '/master/categories', icon: <AppstoreOutlined />, label: 'Categories' },
        { key: '/master/vendors', icon: <TeamOutlined />, label: 'Vendors' },
        { key: '/master/organizations', icon: <ApartmentOutlined />, label: 'Organizations' },
        { key: '/master/warehouses', icon: <ShopOutlined />, label: 'Warehouses' },
        { key: '/master/uom', icon: <CalculatorOutlined />, label: 'Units of Measure' },
        { key: '/master/employees', icon: <UserOutlined />, label: 'Employees' },
        { key: '/master/countries', icon: <GlobalOutlined />, label: 'Countries' },
        { key: '/master/states', icon: <EnvironmentOutlined />, label: 'States' },
        { key: '/master/cities', icon: <HomeOutlined />, label: 'Cities' },
        { key: '/master/areas', icon: <PushpinOutlined />, label: 'Areas' },
        { key: '/master/lookup', icon: <DatabaseOutlined />, label: 'Lookup Values' },
      ],
    },
    {
      key: 'requisition',
      icon: <FileTextOutlined />,
      label: 'Requisitions',
      children: [
        { key: '/requisition', icon: <FileTextOutlined />, label: 'Employee Requisitions' },
        { key: '/requisition/approvals', icon: <CheckCircleOutlined />, label: 'Pending Approvals' },
      ],
    },
    {
      key: 'procurement',
      icon: <ShoppingCartOutlined />,
      label: 'Procurement',
      children: [
        { key: '/purchase', icon: <ShoppingCartOutlined />, label: 'Purchase Orders' },
        { key: '/purchase/approvals', icon: <CheckCircleOutlined />, label: 'PO Approvals' },
      ],
    },
    {
      key: 'stock',
      icon: <InboxOutlined />,
      label: 'Stock Operations',
      children: [
        { key: '/stock/inward', icon: <ImportOutlined />, label: 'Material Inward' },
        { key: '/stock/issue', icon: <ExportOutlined />, label: 'Material Issue' },
        { key: '/stock/transfer', icon: <SwapOutlined />, label: 'Stock Transfer' },
        { key: '/stock/return', icon: <ImportOutlined />, label: 'Material Return' },
      ],
    },
    {
      key: 'field-allocation',
      icon: <CheckCircleOutlined />,
      label: 'Site Usage',
      children: [
        { key: '/field-allocation/site-usage/new', icon: <ExportOutlined />, label: 'Record Site Usage' },
        { key: '/field-allocation/site-usage', icon: <DatabaseOutlined />, label: 'Site Usage History' },
      ],
    },
    {
      key: '/serial',
      icon: <BarcodeOutlined />,
      label: 'Serial Numbers',
    },
    {
      key: 'customer',
      icon: <TeamOutlined />,
      label: 'Customer Management',
      children: [
        { key: '/master/customers', icon: <UserOutlined />, label: 'Local Customers' },
        { key: '/customer-equipment', icon: <UserOutlined />, label: 'Equipment Allocation' },
        { key: '/customer-deallocation', icon: <UserSwitchOutlined />, label: 'Equipment Deallocation' },
        { key: '/qc-queue', icon: <ExperimentOutlined />, label: 'QC Queue' },
      ],
    },
    {
      key: 'reports',
      icon: <FileTextOutlined />,
      label: 'Reports',
      children: [
        { key: '/reports/ledger', label: 'Ledger Report' },
        { key: '/reports/warehouse', label: 'Warehouse Stock' },
        { key: '/reports/movement', label: 'Stock Movement' },
        { key: '/reports/inward', label: 'Inward Report' },
        { key: '/reports/issue', label: 'Issue Report' },
        { key: '/reports/return', label: 'Return Report' },
        { key: '/reports/serial', label: 'Serial Tracking' },
        { key: '/reports/employee-stock', label: 'Employee Stock' },
        { key: '/reports/rejected-serials', label: 'Rejected Serials' },
        { key: '/reports/allocation', label: 'Customer Allocation' },
        { key: '/reports/deallocation', label: 'Customer Deallocation' },
        { key: '/reports/field-allocation', label: 'Field Allocation' },
        { key: '/reports/site-usage', label: 'Site Usage' },
      ],
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      key: 'admin',
      icon: <SafetyOutlined />,
      label: 'Administration',
      children: [
        { key: '/admin/users',       icon: <UserOutlined />,  label: 'Users' },
        { key: '/admin/permissions', icon: <KeyOutlined />,   label: 'Permission Groups' },
      ],
    },
    {
      key: '__api-docs__',
      icon: <FileTextOutlined />,
      label: 'API Docs',
    },
  ];

  const userMenuItems = [
    { key: 'change-password', label: 'Change Password' },
    { type: 'divider' as const },
    { key: 'logout', label: 'Logout' },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === '__api-docs__') {
      window.open('/api/docs', '_blank', 'noopener,noreferrer');
      return;
    }
    if (key === 'change-password') {
      setChangePwdOpen(true);
      return;
    }
    if (key === 'logout') {
      logout();
      return;
    }
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  // Logo component - reusable for sidebar and drawer
  const LogoSection = ({ showText = true }: { showText?: boolean }) => (
    <div
      style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: showText ? 'flex-start' : 'center',
        padding: showText ? '0 16px' : 0,
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <img
        src="/vite.svg"
        alt="Logo"
        style={{ height: 32, width: 32 }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
      {showText && (
        <Text strong style={{ marginLeft: 12, fontSize: 14 }}>
          Inventory
        </Text>
      )}
    </div>
  );

  // Sidebar items filtered by current user's `get` permission bits
  const visibleMenuItems = useMemo(
    () => filterMenuItems(menuItems as AnyMenuItem[], hasPermission, permissionsLoading),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionsLoading, hasPermission],
  );

  // Find the best matching menu item key for the current path (prefix match)
  const getSelectedKey = (pathname: string): string => {
    let bestMatch = '';
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (pathname === child.key || (pathname.startsWith(child.key + '/') && child.key.length > bestMatch.length)) {
            bestMatch = child.key;
          }
        }
      } else {
        if (pathname === item.key || pathname.startsWith(item.key + '/')) {
          bestMatch = item.key;
        }
      }
    }
    return bestMatch || pathname;
  };

  // Find the parent submenu key for a given path
  const getParentKey = (pathname: string): string | null => {
    let bestMatch = '';
    let bestParent: string | null = null;
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if ((pathname === child.key || pathname.startsWith(child.key + '/')) && child.key.length > bestMatch.length) {
            bestMatch = child.key;
            bestParent = item.key;
          }
        }
      }
    }
    return bestParent;
  };

  // Auto-open parent submenu when path changes
  useEffect(() => {
    const parentKey = getParentKey(location.pathname);
    if (parentKey && !openKeys.includes(parentKey)) {
      setOpenKeys((prev) => [...prev, parentKey]);
    }
  }, [location.pathname]);

  const selectedKey = getSelectedKey(location.pathname);

  // Menu component - reusable for sidebar and drawer
  const MenuContent = () => (
    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={(keys) => setOpenKeys(keys as string[])}
      items={visibleMenuItems as any}
      onClick={handleMenuClick}
      style={{ borderRight: 0 }}
    />
  );

  // Calculate content margin based on device and sidebar state
  const getContentMargin = () => {
    if (isMobile) return 0;
    return collapsed ? 80 : 240;
  };

  // Calculate content padding based on screen size
  const getContentPadding = () => {
    if (!screens.sm) return 12; // xs
    if (!screens.md) return 16; // sm
    if (!screens.lg) return 20; // md
    return 24; // lg and above
  };

  return (
    <>
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar - Hidden on mobile */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="light"
          style={{
            borderRight: '1px solid #f0f0f0',
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
          }}
          width={240}
        >
          <LogoSection showText={!collapsed} />
          <MenuContent />
        </Sider>
      )}

      {/* Mobile Drawer Menu */}
      <Drawer
        placement="left"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={280}
        styles={{
          body: { padding: 0 },
          header: { display: 'none' },
        }}
        className="mobile-menu-drawer"
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Space>
            <img
              src="/vite.svg"
              alt="Logo"
              style={{ height: 32, width: 32 }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <Text strong style={{ fontSize: 14 }}>
              Inventory
            </Text>
          </Space>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setDrawerVisible(false)}
          />
        </div>
        <MenuContent />
      </Drawer>

      <Layout
        style={{
          marginLeft: getContentMargin(),
          transition: 'margin-left 0.2s',
        }}
      >
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            height: isMobile ? 56 : 64,
          }}
        >
          <Space>
            {/* Mobile: Hamburger menu button */}
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ fontSize: 18 }}
              />
            ) : (
              /* Desktop: Collapse/expand button */
              React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                onClick: () => setCollapsed(!collapsed),
                style: { fontSize: 18, cursor: 'pointer' },
              })
            )}
            {/* Company name - hide on very small screens */}
            {screens.sm && (
              <Text
                strong
                style={{
                  fontSize: isMobile ? 14 : 16,
                  marginLeft: isMobile ? 8 : 16,
                  maxWidth: isMobile ? 180 : 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isMobile ? 'Inventory' : companyName}
              </Text>
            )}
          </Space>
          <Space size={isMobile ? 'small' : 'large'}>
            <NotificationBell isMobile={isMobile} navigate={navigate} />
            <Dropdown menu={{ items: userMenuItems, onClick: handleMenuClick }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} />
                {/* Hide username on mobile */}
                {!isMobile && <Text>{userName}</Text>}
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content
          style={{
            margin: getContentPadding(),
            minHeight: `calc(100vh - ${isMobile ? 56 : 64}px - ${getContentPadding() * 2}px)`,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>

    <Modal
      title="Change Password"
      open={changePwdOpen}
      onCancel={() => { setChangePwdOpen(false); changePwdForm.resetFields(); }}
      onOk={() => changePwdForm.submit()}
      okText="Change Password"
      confirmLoading={changePwdLoading}
      destroyOnHidden
    >
      <Form
        form={changePwdForm}
        layout="vertical"
        onFinish={handleChangePassword}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[{ required: true, message: 'Please enter your current password' }, { min: 1 }]}
        >
          <Input.Password placeholder="Current password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password placeholder="New password" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" />
        </Form.Item>
      </Form>
    </Modal>
    </>
  );
};

export default MainLayout;
